/**
 * StatusEffectSystem - Handles damage over time and debuff effects
 *
 * Status effects:
 * - bleed: Physical DoT, stacks, more damage when moving
 * - poison: Chaos DoT, stacks infinitely, slower but longer
 * - burn (yanma): Fire DoT, doesn't stack, high damage
 * - chill (soguk): Slows movement and attack speed
 * - freeze (don): Completely immobilizes target
 * - shock (sok): Increases damage taken
 */

export type StatusType = 'bleed' | 'poison' | 'burn' | 'chill' | 'freeze' | 'shock';

export interface StatusEffect {
  id: string;
  type: StatusType;
  sourceId: string;      // Who applied this effect

  // Timing
  duration: number;      // Total duration in seconds
  remaining: number;     // Remaining time
  tickInterval: number;  // How often to tick (for DoTs)
  tickTimer: number;     // Time until next tick

  // Damage (for DoT effects)
  damagePerTick: number;
  damageType: string;

  // Stacking
  stacks: number;
  maxStacks: number;

  // Effect modifiers
  slowPercent?: number;      // For chill
  damageAmplify?: number;    // For shock (% increased damage taken)
}

export interface StatusEffectConfig {
  type: StatusType;
  duration: number;
  baseDamage?: number;
  stacks?: number;
  slowPercent?: number;
  damageAmplify?: number;
}

// Default status effect configurations
export const STATUS_DEFAULTS: Record<StatusType, Partial<StatusEffect>> = {
  bleed: {
    tickInterval: 0.5,       // Tick every 0.5s
    damageType: 'physical',
    maxStacks: 8,             // 8 max stacks (was 10 - slightly less oppressive)
    duration: 4,              // 4s base duration
  },
  poison: {
    tickInterval: 1.0,       // Tick every 1.0s (slow but persistent)
    damageType: 'poison',
    maxStacks: 50,            // 50 max stacks (was 100 - still high but bounded)
    duration: 6,              // 6s base (longer than bleed)
  },
  burn: {
    tickInterval: 0.33,      // Tick every 0.33s (~3 ticks/sec, was 0.25 = 4/sec)
    damageType: 'od',
    maxStacks: 1,             // Burn doesn't stack, refreshes
    duration: 3,              // 3s base duration
  },
  chill: {
    tickInterval: 0,
    damageType: 'su',
    maxStacks: 1,
    slowPercent: 30,          // 30% slow
    duration: 3,              // 3s base
  },
  freeze: {
    tickInterval: 0,
    damageType: 'su',
    maxStacks: 1,
    slowPercent: 100,         // Complete stop
    duration: 1.5,            // 1.5s max (was uncapped - too strong)
  },
  shock: {
    tickInterval: 0,
    damageType: 'gok',
    maxStacks: 1,
    damageAmplify: 35,        // 35% more damage taken (was 50% - too strong)
    duration: 3,              // 3s base
  },
};

// Recommended durations when applying status from skills
export const STATUS_DURATIONS: Record<StatusType, number> = {
  bleed: 4,
  poison: 6,
  burn: 3,
  chill: 3,
  freeze: 1.5,
  shock: 3,
};

/**
 * Create a new status effect
 */
export function createStatusEffect(
  config: StatusEffectConfig,
  sourceId: string
): StatusEffect {
  const defaults = STATUS_DEFAULTS[config.type];
  const id = `${config.type}_${sourceId}_${Date.now()}`;

  return {
    id,
    type: config.type,
    sourceId,
    duration: config.duration,
    remaining: config.duration,
    tickInterval: defaults.tickInterval || 0,
    tickTimer: defaults.tickInterval || 0,
    damagePerTick: config.baseDamage || 0,
    damageType: defaults.damageType || 'physical',
    stacks: config.stacks || 1,
    maxStacks: defaults.maxStacks || 1,
    slowPercent: config.slowPercent ?? defaults.slowPercent,
    damageAmplify: config.damageAmplify ?? defaults.damageAmplify,
  };
}

/**
 * Status Effect Manager - attached to each entity
 */
export class StatusEffectManager {
  private effects: Map<string, StatusEffect> = new Map();

  /**
   * Apply a status effect to this entity
   */
  apply(config: StatusEffectConfig, sourceId: string): void {
    const newEffect = createStatusEffect(config, sourceId);

    // Check for existing effect of same type
    const existingKey = this.findEffectKeyByType(config.type);

    if (existingKey) {
      const existing = this.effects.get(existingKey)!;

      // Handle stacking
      if (existing.maxStacks > 1) {
        // Stack the effect
        existing.stacks = Math.min(existing.stacks + (config.stacks || 1), existing.maxStacks);
        existing.remaining = Math.max(existing.remaining, config.duration);

        // For DoT effects, increase damage with stacks
        if (config.baseDamage) {
          existing.damagePerTick = Math.floor(config.baseDamage * existing.stacks);
        }
      } else {
        // Refresh duration (for non-stacking effects like burn)
        existing.remaining = Math.max(existing.remaining, config.duration);
        existing.damagePerTick = config.baseDamage || existing.damagePerTick;
      }
    } else {
      // Add new effect
      this.effects.set(newEffect.id, newEffect);
    }
  }

  /**
   * Update all effects and return damage to apply
   */
  update(deltaTime: number, isMoving: boolean = false): StatusUpdateResult {
    const result: StatusUpdateResult = {
      damage: 0,
      damageType: 'physical',
      slowPercent: 0,
      damageAmplify: 0,
      isFrozen: false,
      expiredEffects: [],
    };

    const toRemove: string[] = [];

    for (const [key, effect] of this.effects) {
      effect.remaining -= deltaTime;

      // Check expiration
      if (effect.remaining <= 0) {
        toRemove.push(key);
        result.expiredEffects.push(effect.type);
        continue;
      }

      // Process DoT ticks (cap at 3 per frame to prevent burst damage on lag spikes)
      if (effect.tickInterval > 0) {
        effect.tickTimer -= deltaTime;

        let ticksThisFrame = 0;
        while (effect.tickTimer <= 0 && ticksThisFrame < 3) {
          effect.tickTimer += effect.tickInterval;
          ticksThisFrame++;

          let tickDamage = effect.damagePerTick;

          // Bleed does more damage when moving
          if (effect.type === 'bleed' && isMoving) {
            tickDamage = Math.floor(tickDamage * 1.5);
          }

          result.damage += tickDamage;
          result.damageType = effect.damageType;
        }
        // If still behind after cap, reset timer to prevent accumulation
        if (effect.tickTimer <= 0) {
          effect.tickTimer = effect.tickInterval;
        }
      }

      // Apply debuff effects
      if (effect.slowPercent) {
        result.slowPercent = Math.max(result.slowPercent, effect.slowPercent);
      }

      if (effect.damageAmplify) {
        result.damageAmplify += effect.damageAmplify;
      }

      if (effect.type === 'freeze') {
        result.isFrozen = true;
      }
    }

    // Remove expired effects
    for (const key of toRemove) {
      this.effects.delete(key);
    }

    return result;
  }

  /**
   * Remove a specific effect
   */
  remove(type: StatusType): boolean {
    const key = this.findEffectKeyByType(type);
    if (key) {
      this.effects.delete(key);
      return true;
    }
    return false;
  }

  /**
   * Clear all effects
   */
  clear(): void {
    this.effects.clear();
  }

  /**
   * Check if entity has a specific effect
   */
  has(type: StatusType): boolean {
    return this.findEffectKeyByType(type) !== null;
  }

  /**
   * Get effect details
   */
  get(type: StatusType): StatusEffect | null {
    const key = this.findEffectKeyByType(type);
    return key ? this.effects.get(key) || null : null;
  }

  /**
   * Get all active effects
   */
  getAll(): StatusEffect[] {
    return Array.from(this.effects.values());
  }

  /**
   * Get total stacks of a status type
   */
  getStacks(type: StatusType): number {
    const effect = this.get(type);
    return effect ? effect.stacks : 0;
  }

  private findEffectKeyByType(type: StatusType): string | null {
    for (const [key, effect] of this.effects) {
      if (effect.type === type) {
        return key;
      }
    }
    return null;
  }
}

export interface StatusUpdateResult {
  damage: number;
  damageType: string;
  slowPercent: number;
  damageAmplify: number;
  isFrozen: boolean;
  expiredEffects: StatusType[];
}

/**
 * Calculate status effect damage per tick based on source hit damage
 *
 * Design intent:
 * - Bleed: moderate per-tick, compensated by stacking. Total = 70% × stacks over 4s
 * - Poison: low per-tick, compensated by many stacks. Total = 50% × stacks over 6s
 * - Burn: high per-tick, no stacking. Total = 120% over 3s (strongest single-target DoT)
 *
 * These are damage-per-tick values, not total damage.
 * Total DoT damage = damagePerTick × (duration / tickInterval)
 */
export function calculateStatusDamage(
  baseDamage: number,
  statusType: StatusType,
  _sourceDamageMultiplier: number = 1
): number {
  switch (statusType) {
    case 'bleed':
      // Per tick (0.5s interval, 4s duration = 8 ticks): total ~70% of hit per stack
      // Per tick = baseDamage × 0.7 / 8 ≈ 8.75%
      return Math.max(1, Math.floor(baseDamage * 0.09));
    case 'poison':
      // Per tick (1.0s interval, 6s duration = 6 ticks): total ~50% of hit per stack
      // Per tick = baseDamage × 0.5 / 6 ≈ 8.3%
      return Math.max(1, Math.floor(baseDamage * 0.08));
    case 'burn':
      // Per tick (0.33s interval, 3s duration ≈ 9 ticks): total ~120% of hit
      // Per tick = baseDamage × 1.2 / 9 ≈ 13.3%
      return Math.max(1, Math.floor(baseDamage * 0.13));
    default:
      return 0;
  }
}
